import { defineStore } from "pinia";
import { Ref, ref } from "vue";
import {
  IHrStaffFilter,
  INewOffPayload,
  IUpdateAdvancePayload,
  IUpdateBonusPayload,
  IUpdateRatingPayload,
  IUpdateSalaryPayload,
} from "@/features/management/management.types";
import { DateTime } from "luxon";
import { managementService } from "@/features/management/management.service";
import { useNotification } from "@/utils/notification";
import { ITableHeader } from "@/types/table";

const { pushNotification } = useNotification();

export const useManagementStore = defineStore("management", () => {
  const now = ref(DateTime.now().setZone("America/New_York"));
  const managementFilter = ref<IHrStaffFilter>({
    year: now.value.year,
    month: now.value.month,
    q: "",
    shift_template_id: null,
    team_id: null,
    is_active: null,
    view: "",
    page: 1,
    per_page: 50,
  });

  const advanceRequestHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      width: "5%",
    },
    {
      name: "user_name",
      label: "User",
      width: "30%",
    },
    {
      name: "amount",
      label: "Amount",
      width: "25%",
    },
    {
      name: "created_time",
      label: "Created time",
      width: "30%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "10%",
    },
  ];

  async function getHrList(loading: Ref<boolean>, cl: any) {
    loading.value = true;

    await managementService
      .getList({ ...managementFilter.value })
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getAdvanceRequestsList(loading: Ref<boolean>, cl: any) {
    loading.value = true;

    await managementService
      .advanceRequest({
        year: managementFilter.value.year,
        month: managementFilter.value.month,
        page: managementFilter.value.page,
        per_page: managementFilter.value.per_page,
        status: "requested",
        q: managementFilter.value.q,
      })
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function getUserDetail(
    loading: Ref<boolean>,
    user_id: string,
    filter: any,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .getUserDetail(user_id, filter)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function updateSalary(
    loading: Ref<boolean>,
    id: string,
    payload: IUpdateSalaryPayload,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .updateSalary(id, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function updateAdvance(
    loading: Ref<boolean>,
    id: string,
    payload: IUpdateAdvancePayload,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .updateAdvance(id, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function approveAdvance(
    loading: Ref<boolean>,
    user_id: string,
    advance_id: string,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .approveAdvance(user_id, advance_id)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function rejectAdvance(
    loading: Ref<boolean>,
    user_id: string,
    advance_id: string,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .rejectAdvance(user_id, advance_id)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function updateBonus(
    loading: Ref<boolean>,
    id: string,
    payload: IUpdateBonusPayload,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .updateBonus(id, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function updateRating(
    loading: Ref<boolean>,
    id: string,
    payload: IUpdateRatingPayload,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .updateRating(id, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function createOff(
    loading: Ref<boolean>,
    id: string,
    payload: INewOffPayload,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .createOff(id, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function updateOff(
    loading: Ref<boolean>,
    user_id: string,
    off_id: string,
    payload: INewOffPayload,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .updateOff(user_id, off_id, payload)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  async function deleteOff(
    loading: Ref<boolean>,
    user_id: string,
    off_id: string,
    cl: any
  ) {
    loading.value = true;

    await managementService
      .deleteOff(user_id, off_id)
      .then((res) => {
        cl(res.data);
      })
      .catch((err) => {
        pushNotification(err.response?.data?.detail || err, "error");
      })
      .finally(() => {
        loading.value = false;
      });
  }

  return {
    now,
    managementFilter,
    advanceRequestHeaders,
    getHrList,
    getAdvanceRequestsList,
    getUserDetail,
    updateSalary,
    updateAdvance,
    approveAdvance,
    rejectAdvance,
    updateBonus,
    updateRating,
    createOff,
    updateOff,
    deleteOff,
  };
});
